import {
  format, parseISO, isValid,
  startOfDay, endOfDay,
  startOfWeek, startOfMonth, startOfYear, startOfQuarter,
  subDays, eachDayOfInterval, eachMonthOfInterval,
} from 'date-fns'

export const DATE_FORMAT = 'yyyy-MM-dd'
export const DISPLAY_FORMAT = 'dd MMM yyyy'
export const DISPLAY_DATETIME = 'dd MMM yyyy, HH:mm'

export function toAPIDate(date: Date | string): string {
  const d = typeof date === 'string' ? parseISO(date) : date
  return isValid(d) ? format(d, DATE_FORMAT) : ''
}

export function displayDate(date: string | Date | null | undefined): string {
  if (!date) return '—'
  const d = typeof date === 'string' ? parseISO(date) : date
  return isValid(d) ? format(d, DISPLAY_FORMAT) : '—'
}

export function displayDateTime(date: string | Date | null | undefined): string {
  if (!date) return '—'
  const d = typeof date === 'string' ? parseISO(date) : date
  return isValid(d) ? format(d, DISPLAY_DATETIME) : '—'
}

/**
 * The "posted" date (when a row was actually entered into Palstro, i.e. created_at),
 * formatted for display — but ONLY when it falls on a different day from the business
 * date (entry_date). Returns '' when they match, so callers can render a muted dash.
 * Keeps the audit-relevant posted date visible for backdated entries without adding
 * noise to same-day rows.
 */
export function postedDateLabel(
  createdAt: string | null | undefined,
  entryDate: string | null | undefined,
): string {
  if (!createdAt) return ''
  const posted = createdAt.slice(0, 10)
  const business = entryDate || posted
  return posted === business ? '' : displayDate(createdAt)
}

export function today(): string {
  return format(new Date(), DATE_FORMAT)
}

export function firstDayOfMonth(): string {
  return format(startOfMonth(new Date()), DATE_FORMAT)
}

export type TimeTab = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom'

export function getDateRange(tab: TimeTab, customFrom?: string, customTo?: string): { from: string; to: string } {
  const now = new Date()
  switch (tab) {
    case 'today':
      return { from: format(startOfDay(now), DATE_FORMAT), to: format(endOfDay(now), DATE_FORMAT) }
    case 'week':
      return { from: format(startOfWeek(now, { weekStartsOn: 1 }), DATE_FORMAT), to: format(endOfDay(now), DATE_FORMAT) }
    case 'month':
      return { from: format(startOfMonth(now), DATE_FORMAT), to: format(endOfDay(now), DATE_FORMAT) }
    case 'quarter':
      return { from: format(startOfQuarter(now), DATE_FORMAT), to: format(endOfDay(now), DATE_FORMAT) }
    case 'year':
      return { from: format(startOfYear(now), DATE_FORMAT), to: format(endOfDay(now), DATE_FORMAT) }
    case 'custom':
      return {
        from: customFrom ?? format(startOfMonth(now), DATE_FORMAT),
        to: customTo ?? format(endOfDay(now), DATE_FORMAT),
      }
  }
}

/** Build a 12-element array of monthly totals for a given year from an array of {created_at, total} rows */
export function aggregateByMonth(
  rows: Array<{ created_at: string; total?: number | null }>,
  year: number,
  label: string,
): Array<{ month: string; [key: string]: number | string }> {
  const months = eachMonthOfInterval({
    start: new Date(year, 0, 1),
    end: new Date(year, 11, 31),
  })
  return months.map((m) => {
    const key = format(m, 'yyyy-MM')
    const total = rows
      .filter((r) => r.created_at.startsWith(key))
      .reduce((sum, r) => sum + (r.total ?? 0), 0)
    return { month: format(m, 'MMM'), [label]: total }
  })
}

/** Build a 30-element array of daily totals */
export function aggregateByDay(
  rows: Array<{ created_at: string; total?: number | null }>,
): Array<{ day: string; sales: number }> {
  const end = new Date()
  const start = subDays(end, 29)
  const days = eachDayOfInterval({ start, end })
  return days.map((d) => {
    const key = format(d, DATE_FORMAT)
    const total = rows
      .filter((r) => r.created_at.startsWith(key))
      .reduce((sum, r) => sum + (r.total ?? 0), 0)
    return { day: format(d, 'd MMM'), sales: total }
  })
}
